// =============================================================================
// SIREPPAR - Selector de agrupación (partido / confederación / alianza)
// =============================================================================
// Reemplaza al <select> nativo donde hay que elegir a qué agrupación pertenece
// algo. Tres cosas que el nativo no daba:
//
// 1. DENOMINACIÓN, no sigla. El nativo mostraba `sigla || denominacion`, así que
//    la mayoría de las opciones eran tres letras: para elegir había que saberse
//    las siglas de memoria. La sigla se sigue viendo, al costado y en gris.
// 2. LAS TRES CLASES. Las alianzas y confederaciones son filas de `partidos` y el
//    listado las excluye por defecto; acá se piden con `clase=TODAS`, porque un
//    documento puede pertenecer a cualquiera de las tres.
// 3. BÚSQUEDA. Con decenas de agrupaciones, escribir es más rápido que scrollear.
//    Filtra por denominación, sigla y número, ignorando acentos y mayúsculas.
//
// Mismo mecanismo de panel que TipoDocumentoSelect (PopoverPortal + input como
// disparador), por el mismo motivo: dentro de un modal el desplegable quedaba
// recortado, y el panel en un portal no puede tener el foco porque el Dialog de
// Headless UI se lo devuelve al modal.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import PopoverPortal from './PopoverPortal';
import { normalizarTexto, coincideBusqueda } from '../../utils/busqueda';

export interface AgrupacionOpcion {
  id: string;
  numero?: number | null;
  denominacion: string;
  sigla?: string | null;
  clase?: string | null;
  /**
   * Segunda línea a medida, cuando la clase no es el dato que distingue. En el
   * wizard de alianza hay municipales homónimos de distintas localidades: sin la
   * localidad a la vista no hay forma de elegir. Reemplaza a la clase y también
   * entra en la búsqueda.
   */
  detalle?: string | null;
}

interface Props {
  value: string;
  onChange: (id: string) => void;
  opciones: AgrupacionOpcion[];
  placeholder?: string;
  /** Texto de la opción vacía. Si no se pasa, no se ofrece "sin agrupación". */
  labelVacio?: string;
  disabled?: boolean;
}

const PANEL_MAX_HEIGHT = 320;
const PANEL_MIN_WIDTH = 320;

const CLASE_LABEL: Record<string, string> = {
  CONFEDERACION: 'Confederación',
  ALIANZA: 'Alianza',
};

export default function AgrupacionSelect({
  value, onChange, opciones, placeholder = 'Buscá por nombre, sigla o número…', labelVacio, disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [resaltado, setResaltado] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const etiqueta = (o: AgrupacionOpcion) =>
    `${o.numero ? `${o.numero} — ` : ''}${o.denominacion}`;

  const filtrados = useMemo(() => {
    const q = busqueda.trim();
    if (!q) return opciones;
    const primera = normalizarTexto(q).split(/\s+/)[0];
    const empiezan: AgrupacionOpcion[] = [];
    const contienen: AgrupacionOpcion[] = [];
    for (const o of opciones) {
      const heno = `${o.denominacion} ${o.sigla || ''} ${o.numero ?? ''} ${o.detalle || ''}`;
      if (!coincideBusqueda(q, heno)) continue;
      // Los que EMPIEZAN con lo escrito van primero: buscar "frente" tiene que
      // poner arriba "Frente…" y no "Partido del Frente…".
      const arranca = normalizarTexto(o.denominacion).startsWith(primera)
        || normalizarTexto(o.sigla || '').startsWith(primera)
        || String(o.numero ?? '').startsWith(primera);
      (arranca ? empiezan : contienen).push(o);
    }
    return [...empiezan, ...contienen];
  }, [busqueda, opciones]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => { setResaltado(0); }, [busqueda]);
  useEffect(() => {
    if (!open) return;
    document.querySelectorAll('[data-portal-dropdown] li')[resaltado]?.scrollIntoView({ block: 'nearest' });
  }, [resaltado, open]);

  const abrir = () => {
    if (disabled) return;
    setBusqueda('');
    setResaltado(Math.max(0, filtrados.findIndex((o) => o.id === value)));
    setOpen(true);
  };

  const elegir = (id: string) => { onChange(id); setOpen(false); setBusqueda(''); };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setResaltado((i) => Math.min(i + 1, filtrados.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setResaltado((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      // Enter dentro de un modal no debe disparar el guardar de atrás.
      e.stopPropagation();
      const elegido = filtrados[resaltado];
      if (elegido) elegir(elegido.id);
    }
  };

  const elegida = opciones.find((o) => o.id === value);
  const label = elegida ? etiqueta(elegida) : (value === '' && labelVacio ? labelVacio : '');

  return (
    <div ref={containerRef} className="relative">
      <div className={`w-full flex items-center gap-2 rounded-lg border border-gray-300 bg-white pl-3 pr-2 py-2 ${disabled ? 'opacity-60' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          value={open ? busqueda : label}
          onChange={(e) => { if (open) setBusqueda(e.target.value); }}
          onFocus={() => { if (!open) abrir(); }}
          onClick={() => { if (!open) abrir(); }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={label || placeholder}
          className="flex-1 min-w-0 text-sm bg-transparent focus:outline-none placeholder:text-gray-500"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => (open ? setOpen(false) : (inputRef.current?.focus(), abrir()))}
          className="flex-shrink-0"
          aria-label={open ? 'Cerrar la lista' : 'Abrir la lista'}
        >
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <PopoverPortal anchorRef={containerRef} open={open} onClose={() => setOpen(false)} maxHeight={PANEL_MAX_HEIGHT} minWidth={PANEL_MIN_WIDTH}>
        <ul className="overflow-y-auto py-1">
          {labelVacio && (
            <li>
              <button
                type="button"
                onClick={() => elegir('')}
                className={`w-full text-left px-3 py-2 text-sm ${value === '' ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-500'}`}
              >
                {labelVacio}
              </button>
            </li>
          )}
          {filtrados.length === 0 && (
            <li className="px-3 py-3 text-sm text-gray-400">Ninguna agrupación coincide con “{busqueda}”.</li>
          )}
          {filtrados.map((o, idx) => (
            <li key={o.id}>
              <button
                type="button"
                onMouseEnter={() => setResaltado(idx)}
                onClick={() => elegir(o.id)}
                className={`w-full text-left px-3 py-2 text-sm ${
                  o.id === value
                    ? 'bg-blue-50 font-medium text-blue-700'
                    : idx === resaltado ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                }`}
              >
                <span className="block truncate">{etiqueta(o)}</span>
                <span className="block text-xs text-gray-400">
                  {[o.sigla, o.detalle || (o.clase && CLASE_LABEL[o.clase])].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverPortal>
    </div>
  );
}
